export const QUESTIONS = [
  { id: 1, type: 1, text: "Are processes and rules prioritized over people, leading to rigid micromanagement?" },
  { id: 2, type: 1, text: "Is there a pervasive fear of making mistakes that stifles innovation and risk-taking?" },
  { id: 3, type: 2, text: "Is critical feedback frequently withheld to avoid hurting feelings or damaging relationships?" },
  { id: 4, type: 2, text: "Do team members overcommit to helping others, leading to burnout and missed personal targets?" },
  { id: 5, type: 3, text: "Is the team overly focused on optics and looking successful, sometimes hiding actual failures?" },
  { id: 6, type: 3, text: "Are targets and metrics pushed so hard that team well-being and sustainability are sacrificed?" },
  { id: 7, type: 4, text: "Does interpersonal drama or moodiness often derail objective, business-critical decision-making?" },
  { id: 8, type: 4, text: "Is there a tendency to focus on what is missing or flawed rather than leveraging what is working well?" },
  { id: 9, type: 5, text: "Do team members operate in silos, hoarding information rather than sharing it proactively?" },
  { id: 10, type: 5, text: "Is action frequently delayed by the need to over-analyze and gather endless amounts of data?" },
  { id: 11, type: 6, text: "Is there widespread anxiety or 'worst-case scenario' thinking that paralyzes new initiatives?" },
  { id: 12, type: 6, text: "Are decisions repeatedly second-guessed and run by multiple committees before approval?" },
  { id: 13, type: 7, text: "Does the team constantly chase shiny new ideas but struggle to execute and finish current projects?" },
  { id: 14, type: 7, text: "Are difficult or boring operational realities ignored in favor of exciting future visions?" },
  { id: 15, type: 8, text: "Are disagreements handled with confrontation, leading to a culture of intimidation or fear?" },
  { id: 16, type: 8, text: "Do team members hesitate to speak up because they fear a harsh, dominating, or dismissive response?" },
  { id: 17, type: 9, text: "Are critical, underlying issues swept under the rug to maintain an artificial sense of harmony?" },
  { id: 18, type: 9, text: "Does the team struggle to set clear, decisive priorities, treating everything as equally important?" }
];

export const TYPE_DESCRIPTIONS: Record<number, { 
  name: string, 
  description: string, 
  positive: string,
  strengths: string[],
  challenges: string[],
  fiveLensValue: {
    title: string,
    description: string
  }
}> = {
  1: { 
    name: "The Strict Perfectionist", 
    description: "Your team may be operating under a culture of rigidity and fear of failure. When Enneagram Type 1 energy becomes unhealthy, it stifles creativity and creates a bottleneck of micromanagement.",
    positive: "However, this same energy, when healthy, means your team is incredibly detail-oriented, holds extremely high standards for quality, and possesses a strong ethical compass. You have the structural foundation for operational excellence if the fear of mistakes can be managed.",
    strengths: [
      "Uncompromising quality standards and attention to detail",
      "Clear operational procedures and strong organizational structure",
      "Deeply ethical compliance and accountability",
      "Ability to execute complex tasks with high precision"
    ],
    challenges: [
      "Micromanagement bottlenecks that delay project delivery",
      "Fear of taking calculated risks, stifling innovation",
      "Rigidity in the face of unexpected change or ambiguity",
      "Highly critical environment leading to team burnout and low morale"
    ],
    fiveLensValue: {
      title: "Shifting from Rigid Perfectionism to Agile Excellence",
      description: "We help Type 1 dominant teams differentiate between 'good enough to move forward' and 'critical flaws.' Using our Personal Mastery and Emotional Resilience lenses, we lower the underlying anxiety driving micromanagement, enabling leaders to delegate effectively and foster a culture of innovative risk-taking without compromising quality."
    }
  },
  2: { 
    name: "The Over-Accommodator", 
    description: "Your team is prioritizing harmony over honesty. When Enneagram Type 2 energy is out of balance, crucial feedback is avoided, and people-pleasing leads to burnout and hidden resentment.",
    positive: "At its core, your team possesses profound empathy, strong relational intelligence, and a deep commitment to supporting one another. When balanced with clear boundaries, this creates an incredibly loyal, collaborative, and psychologically safe environment.",
    strengths: [
      "High psychological safety and strong team cohesion",
      "Incredible customer service and client relationship building",
      "Deep empathy for team well-being and mutual support",
      "Strong inter-departmental loyalty and collaboration"
    ],
    challenges: [
      "Avoidance of critical feedback, allowing poor performance to persist",
      "Hidden resentments and passive-aggressiveness due to unexpressed needs",
      "Severe burnout from lack of boundaries and over-committing",
      "Delayed decision-making in an attempt to maintain universal consensus"
    ],
    fiveLensValue: {
      title: "Transforming People-Pleasing into Purposeful Candor",
      description: "We guide Type 2 cultures to embrace 'radical candor' as a form of genuine care. By leveraging our Social Drives framework, we help teams establish clear professional boundaries and communication frameworks, enabling them to deliver robust constructive feedback without fracturing trust or relationships."
    }
  },
  3: { 
    name: "The Relentless Achiever", 
    description: "Your team is overly focused on image and metrics at the expense of substance and well-being. Unhealthy Enneagram Type 3 dynamics can lead to cutting corners and hiding failures to look good.",
    positive: "Your team has an exceptional drive for success, high adaptability, and the capacity to execute goals efficiently. Underneath the pressure is a highly motivated, pragmatic engine capable of moving mountains when aligned with authentic, sustainable objectives.",
    strengths: [
      "High-velocity execution and exceptional productivity",
      "Extreme adaptability to shifting market demands",
      "Fierce goal-oriented focus and pragmatic problem-solving",
      "Strong competitive drive to be industry leaders"
    ],
    challenges: [
      "Cutting corners or sacrificing quality to hit aggressive metrics",
      "Hiding failures or shifting blame to protect the team's image",
      "Toxic competitiveness prioritizing individual glory over collaboration",
      "Severe burnout hidden behind a successful, polished facade"
    ],
    fiveLensValue: {
      title: "Moving from Metric-Chasing to Sustainable Success",
      description: "We help high-performing Type 3 teams transition from superficial optics to authentic, sustainable success. Through our Energy Centres analysis, we guide teams to reconnect with their core purpose, reducing the need for constant external validation and fostering genuine collaborative innovation rather than cutthroat competition."
    }
  },
  4: { 
    name: "The Individualistic Dynamic", 
    description: "Your team may be experiencing emotional turbulence that derails execution. Unhealthy Enneagram Type 4 energy can lead to focusing on flaws and subjective feelings rather than objective business realities.",
    positive: "The underlying strength of this dynamic is immense creativity, a desire for authentic connection, and the ability to find innovative, non-obvious solutions. Your team is not satisfied with the mundane and has the potential to pioneer truly unique market offerings.",
    strengths: [
      "Pioneering creativity and out-of-the-box thinking",
      "High emotional intelligence and empathy for nuanced problems",
      "Ability to create deep, authentic connections with clients",
      "Strong focus on organizational purpose, meaning, and aesthetics"
    ],
    challenges: [
      "Emotional volatility that frequently derails objective meetings",
      "Resistance to standardizing processes or adopting 'mundane' routines",
      "Over-complicating straightforward tasks to make them unique",
      "Tendency to focus on what is missing rather than leveraging what works"
    ],
    fiveLensValue: {
      title: "Channeling Creative Turbulence into Structured Innovation",
      description: "We help Type 4 dominant teams harness their creative brilliance without getting bogged down in interpersonal drama. By developing Emotional Resilience, we train leaders to separate objective business realities from subjective feelings, enabling them to execute brilliant ideas reliably and consistently."
    }
  },
  5: { 
    name: "The Detached Observer", 
    description: "Your team is struggling with information silos and analysis paralysis. When Enneagram Type 5 energy dominates, action is delayed, and collaboration breaks down due to resource hoarding.",
    positive: "This team possesses formidable analytical capabilities, deep subject matter expertise, and a remarkable ability to remain objective during crises. You have brilliant strategic thinkers who can solve the most complex problems when encouraged to actively share their insights.",
    strengths: [
      "Unmatched analytical depth and data-driven decision making",
      "Objective, calm crisis management and troubleshooting",
      "Deep subject matter expertise and intellectual rigor",
      "Highly strategic, long-term architectural planning"
    ],
    challenges: [
      "Information hoarding resulting in severe departmental silos",
      "Analysis paralysis that continually delays time-to-market",
      "Emotional detachment causing poor cross-functional collaboration",
      "Reluctance to share 'half-baked' ideas, stifling early-stage innovation"
    ],
    fiveLensValue: {
      title: "Bridging the Gap Between Analysis and Action",
      description: "We help Type 5 teams unlock the emotional and physical energy required to move from theoretical planning to collaborative execution. Using the FIVE LENS framework, we establish safe, structured communication rhythms that break down silos and encourage the active sharing of knowledge across the organization."
    }
  },
  6: { 
    name: "The Anxious Skeptic", 
    description: "Your team is paralyzed by risk aversion and second-guessing. Unhealthy Enneagram Type 6 energy manifests as endless committee reviews, fear of blame, and a lack of decisive action.",
    positive: "Your team's inherent strength is unparalleled risk mitigation, fierce loyalty, and excellent contingency planning. They have an incredible capacity for troubleshooting and will fiercely protect the organization once they feel secure and supported.",
    strengths: [
      "Exceptional risk mitigation and contingency planning",
      "Fierce loyalty, dedication, and protective commitment to the organization",
      "Highly responsible, thorough, and reliable execution",
      "Ability to foresee and troubleshoot complex systemic issues"
    ],
    challenges: [
      "Paralysis by committee, requiring too much consensus to move forward",
      "Excessive worst-case scenario thinking that kills new initiatives",
      "Resistance to necessary organizational change or new leadership",
      "A culture of suspicion, second-guessing, or fear of blame"
    ],
    fiveLensValue: {
      title: "Turning Anxious Skepticism into Confident Foresight",
      description: "We help Type 6 teams build internal psychological safety so they don't have to rely entirely on endless committees and external rules. Our framework builds Personal Mastery to increase tolerance for ambiguity and risk, empowering leaders to make decisive, strategic moves with confidence."
    }
  },
  7: { 
    name: "The Scattered Enthusiast", 
    description: "Your team struggles with execution and follow-through. When Enneagram Type 7 energy is unfocused, there is a constant chase for the 'next big thing' while current operational realities are ignored.",
    positive: "This team brings unmatched energy, visionary thinking, and extreme agility to the table. They are highly adaptable innovators who can rapidly pivot and enthusiastically champion new initiatives when provided with strong structural anchors.",
    strengths: [
      "Rapid ideation, brainstorming, and visionary thinking",
      "High agility and adaptability to changing market conditions",
      "Infectious enthusiasm that can easily rally the wider organization",
      "Ability to synthesize disparate ideas into innovative solutions"
    ],
    challenges: [
      "Poor follow-through on key initiatives and operational deliverables",
      "Ignoring mundane but critical tasks in favor of exciting new projects",
      "Constantly shifting priorities that exhaust support staff",
      "Avoiding difficult conversations or sweeping systemic issues under the rug"
    ],
    fiveLensValue: {
      title: "Providing the Structural Anchor for Brilliant Ideas",
      description: "We provide the grounding structure that Type 7 teams need to execute their vision. By analyzing Social Drives and Energy Centres, we help them develop the discipline to stay the course, hold each other accountable, and face operational realities without losing their innovative spark."
    }
  },
  8: { 
    name: "The Dominating Challenger", 
    description: "Your team culture may be suffering from intimidation and conflict. Unhealthy Enneagram Type 8 dynamics create an environment where only the loudest voices win, and psychological safety is destroyed.",
    positive: "Beneath the friction lies tremendous leadership capacity, raw energy, and the courage to make tough, necessary decisions. This team is fiercely protective of its people and goals, capable of driving massive change when power is channeled toward empowering others.",
    strengths: [
      "Decisive action in crises and willingness to make tough calls",
      "Bold market moves and fearless strategic positioning",
      "Fierce protection of their people and organizational boundaries",
      "Ability to push through massive obstacles and execute rapidly"
    ],
    challenges: [
      "Intimidating communication styles that silence dissenting opinions",
      "Low psychological safety, resulting in a 'my way or the highway' culture",
      "Creating an exhaustingly combative and high-conflict environment",
      "Failure to recognize the emotional toll their intensity takes on others"
    ],
    fiveLensValue: {
      title: "Channeling Raw Power into Empowering Leadership",
      description: "We help powerful Type 8 cultures calibrate their intensity and channel their raw energy into empowering their people. By developing Emotional Resilience and Personal Mastery, we teach them to invite diverse perspectives, show vulnerability, and build a culture of robust, healthy debate rather than fear-based compliance."
    }
  },
  9: { 
    name: "The Conflict-Avoidant Peacemaker", 
    description: "Your team is suffering from artificial harmony. When Enneagram Type 9 energy is misaligned, critical issues are ignored, and a lack of clear prioritization leads to stagnation.",
    positive: "Your team has an innate ability to see all sides of an issue, creating inclusive environments where everyone feels heard. When they step into healthy assertiveness, they are masterful mediators and steady, grounding forces during times of organizational upheaval.",
    strengths: [
      "Highly inclusive decision-making that considers all viewpoints",
      "Steady, calm, and grounding presence under extreme pressure",
      "Masterful mediation of diverse teams and complex stakeholder relationships",
      "Creation of harmonious, highly supportive work environments"
    ],
    challenges: [
      "Artificial harmony that masks deep organizational dysfunction",
      "Chronic procrastination on tough, necessary decisions",
      "Passive resistance to change through inaction or 'forgetting'",
      "Lack of clear strategic prioritization, treating everything as equally important"
    ],
    fiveLensValue: {
      title: "Transforming Artificial Harmony into Productive Alignment",
      description: "We help Type 9 teams find their voice and step into healthy, necessary conflict. Using the FIVE LENS framework, we uncover the hidden friction beneath the surface and provide practical tools for assertive communication, ensuring that critical issues are addressed directly and clear strategic priorities are established."
    }
  }
};
